import { Component, computed, effect, inject, Input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatDrawer } from '@angular/material/sidenav';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { PhonePipe } from '@jfw/angular/shared/pipes';
import { AccountService } from '@jfw/angular/shared/services';
import {
  DealerUserService,
  PlatformCommunicationQueueService,
} from '@jfw/angular/platform/services';
import { PLATFORM_PORTAL_ENVIRONMENT } from '@jfw/angular/platform/tokens';
import {
  DealerUser,
  ecomStatusLabel,
  eliteStatusLabel,
  establishedDateLabel,
  isValidSite,
  PermissionEnum,
  Site,
} from '@jfw/types';
import {
  convertUnixTimestampToPrettyDate,
  getCurrentUnixTimestamp,
} from '@jfw/business-logic';
import { Subject } from 'rxjs';
import { concatMap, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../../../../services/auth/auth.service';
import {
  AddDealerUserDialogComponent,
  AddDealerUserDialogData,
} from '../add-dealer-user-dialog/add-dealer-user-dialog.component';

type InviteUserRequest = {
  dealerUser: DealerUser;
  site: Site;
};

type UserStatus = {
  colorClass: 'green' | 'yellow' | 'red';
  toolTip: string;
};

const ONE_WEEK_SECONDS = 7 * 24 * 60 * 60;

// "Elite · Platinum" -> "Platinum": the drawer's row label already names it.
function stripPrefix(label: string | null): string | null {
  return label?.split(' · ').slice(1).join(' · ') || label;
}

@Component({
  selector: 'app-account-store-info',
  templateUrl: './account-store-info.component.html',
  styleUrls: ['./account-store-info.component.scss'],
  imports: [PhonePipe, MatSnackBarModule, MatTooltipModule, NgClass],
})
export class AccountStoreInfoComponent {
  private accountService: AccountService = inject(AccountService);
  private dealerUserService = inject(DealerUserService);
  private communicationQueueService = inject(PlatformCommunicationQueueService);
  private authService = inject(AuthService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private environment = inject(PLATFORM_PORTAL_ENVIRONMENT);
  @Input() drawer?: MatDrawer;

  platformAccount = this.accountService._platformAccount;

  // EXT-6537: same labels as the account bar. The tag text there reads
  // "Elite · Platinum"; here the row label already says Elite, so just the tier.
  eliteLabel = computed(() =>
    stripPrefix(eliteStatusLabel(this.platformAccount()?.eliteStatus)),
  );
  ecomLabel = computed(() =>
    stripPrefix(ecomStatusLabel(this.platformAccount()?.ecomStatus)),
  );
  established = computed(() =>
    establishedDateLabel(this.platformAccount()?.establishedOn),
  );
  drawerState = computed(() => this.accountService.drawerState());

  promotionBalances = this.accountService._promotionBalances;
  promotionsLoading = computed(() => this.accountService.promotionsLoader());
  activePromotionCount = computed(
    () =>
      this.promotionBalances().promotions.filter(
        (promotion) => promotion.active,
      ).length,
  );

  dealerUsers = this.accountService._dealerUsers;
  dealerUsersLoading = computed(() => this.accountService.dealerUsersLoader());
  dealerUsersError = computed(() => this.accountService.dealerUsersError());
  canImpersonateDealerUser = this.authService.hasPermissions([
    PermissionEnum['DealerUser.Impersonate'],
  ]);
  canCreateDealerUser = this.authService.hasPermissions([
    PermissionEnum['DealerUser.Create'],
  ]);

  // Most recently active first; never-logged-in (unregistered or registered
  // but never logged in) users sort last, together.
  sortedDealerUsers = computed(() =>
    [...this.dealerUsers()].sort(
      (a, b) => (b.lastLoggedIn ?? -Infinity) - (a.lastLoggedIn ?? -Infinity),
    ),
  );

  private readonly inviteUserRequest$ = new Subject<InviteUserRequest>();

  protected readonly inviteUserResponse = toSignal(
    this.inviteUserRequest$.pipe(
      concatMap(({ dealerUser, site }) =>
        this.communicationQueueService
          .queueEmailDealerInvite(
            site,
            dealerUser.firstName,
            dealerUser.lastName,
            dealerUser.email,
            dealerUser.documentId,
            dealerUser.displayName,
            dealerUser.userId,
          )
          .pipe(
            switchMap(() =>
              this.dealerUserService.markEmailInviteSent(dealerUser),
            ),
          ),
      ),
      tap((updatedUser) => {
        this.snackBar.open(`Invite sent to ${updatedUser.email}`, 'Dismiss');
      }),
    ),
  );

  constructor() {
    // Re-opening the Account tab remounts this component (it's behind an @if
    // in the parent), which would otherwise re-fetch on every click into the
    // tab -- same remount-on-reopen shape ShippingInfoComponent already solves.
    effect(() => {
      const account = this.platformAccount();
      if (
        account?.accountNumber &&
        !this.accountService.hasLoadedPromotionsFor(account.accountNumber)
      ) {
        this.accountService.markPromotionsLoaded(account.accountNumber);
        this.accountService.loadPromotionBalances(account.accountNumber);
      }
    });

    // Same remount-on-reopen rationale as the promotions effect above.
    effect(() => {
      const account = this.platformAccount();
      if (
        account?.accountNumber &&
        !this.accountService.hasLoadedDealerUsersFor(account.accountNumber)
      ) {
        this.accountService.markDealerUsersLoaded(account.accountNumber);
        this.accountService.loadDealerUsers(account.accountNumber);
      }
    });
  }

  protected impersonateUser(user: DealerUser): void {
    if (!this.canImpersonateDealerUser() || !user.userId) return;
    this.dealerUserService.getImpersonationToken(user.userId).subscribe({
      next: (response) => {
        if (!response.success) {
          this.snackBar.open('Unable to impersonate user.', 'Dismiss');
        }
      },
      error: () => {
        this.snackBar.open('Unable to impersonate user.', 'Dismiss');
      },
    });
  }

  // A dialog rather than navigating to /dealer-user/create: that route is in
  // the Dealer User suite, which closes this drawer. The dialog refreshes
  // the Users list itself on save.
  protected addUser(): void {
    const accountNumber = this.platformAccount()?.accountNumber;
    if (!this.canCreateDealerUser() || !accountNumber) return;
    this.dialog.open<AddDealerUserDialogComponent, AddDealerUserDialogData>(
      AddDealerUserDialogComponent,
      {
        data: { accountNumber },
        width: '600px',
        maxWidth: '95vw',
      },
    );
  }

  protected resendInvite(user: DealerUser): void {
    if (user.userId) return;
    const site = this.environment.site;
    if (!isValidSite(site)) {
      throw new Error('Invalid site in environment.ts.');
    }
    this.inviteUserRequest$.next({ dealerUser: user, site });
  }

  // Green: registered and logged in within the last week. Yellow: registered
  // but not recently active. Red: hasn't registered at all yet.
  protected getUserStatus(user: DealerUser): UserStatus {
    const toolTip = `Last Logged In: ${
      user.lastLoggedIn
        ? convertUnixTimestampToPrettyDate(user.lastLoggedIn)
        : 'Never'
    }`;

    if (!user.userId) {
      return { colorClass: 'red', toolTip };
    }
    if (
      user.lastLoggedIn &&
      user.lastLoggedIn >= getCurrentUnixTimestamp() - ONE_WEEK_SECONDS
    ) {
      return { colorClass: 'green', toolTip };
    }
    return { colorClass: 'yellow', toolTip };
  }

  protected getUserRole(user: DealerUser): string {
    const accountNumber = this.platformAccount()?.accountNumber;
    return (
      user.dealers.find((dealer) => dealer.dealerId === accountNumber)?.role ??
      ''
    );
  }

  protected get address() {
    const account = this.platformAccount();
    if (!account) return {};

    const { city, state, zip, streetAddress1, streetAddress2 } =
      account?.address || {};
    return { city, state, zip, streetAddress1, streetAddress2 };
  }
}
